import { isoZ, z } from 'my-zod'
import { enumOptionsToObject } from '../../internal'
import { match } from 'ts-pattern'

const vehicleUseMetricCollectionConfiguration = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('alwaysCollect')
  }),
  z.object({
    type: z.literal('neverCollect')
  }),
  z.object({
    type: z.literal('skipCollectionForProducts'),
    productIds: z.string().array().min(1)
  })
])

const configurationTypeOptions = ['alwaysCollect', 'neverCollect', 'skipCollectionForProducts'] as const satisfies Readonly<
  z.infer<typeof vehicleUseMetricCollectionConfiguration>['type'][]
>
const configurationTypes = enumOptionsToObject(configurationTypeOptions)

const vehicleUseMetricCollectionConfigurationModel = z.object({
  outfitterId: z.number(),
  configuration: vehicleUseMetricCollectionConfiguration,
  createdDate: isoZ.instant(),
  lastModifiedDate: isoZ.instant()
})

const vehicleUseMetricCollectionConfigurationUpdate = vehicleUseMetricCollectionConfigurationModel.pick({
  outfitterId: true,
  configuration: true
})

const maxDaysWithoutMetricsCollection = 7

function isExpeditedCheckOut({
  configuration,
  productId
}: {
  configuration: z.infer<typeof vehicleUseMetricCollectionConfiguration>
  productId: string | null
}): boolean {
  return match(configuration ?? { type: 'alwaysCollect' })
    .with({ type: 'alwaysCollect' }, () => false)
    .with({ type: 'neverCollect' }, () => true)
    .with({ type: 'skipCollectionForProducts' }, (c) => {
      if (c.productIds.includes(productId as string)) {
        return true
      } else {
        return false
      }
    })
    .exhaustive()
}

function isMetricCollectionRequired({
  daysSinceVehicleImageCapture,
  ...options
}: {
  configuration: z.infer<typeof vehicleUseMetricCollectionConfiguration>
  productId: string | null
  daysSinceVehicleImageCapture: number | null
}): boolean {
  const expedited = isExpeditedCheckOut(options)
  const isPastMaxDays =
    daysSinceVehicleImageCapture === null || daysSinceVehicleImageCapture >= maxDaysWithoutMetricsCollection
  return !expedited || isPastMaxDays
}

export const VehicleUseMetricCollectionConfigurationSchemas = {
  vehicleUseMetricCollectionConfigurationModel,
  vehicleUseMetricCollectionConfiguration,
  vehicleUseMetricCollectionConfigurationUpdate,
  isMetricCollectionRequired,
  isExpeditedCheckOut,
  configurationTypeOptions,
  configurationTypes,
  maxDaysWithoutMetricsCollection
}

export declare namespace VehicleUseMetricCollectionConfigurationSchemas {
  export type vehicleUseMetricCollectionConfigurationModel = z.infer<typeof vehicleUseMetricCollectionConfigurationModel>
  export type vehicleUseMetricCollectionConfiguration = z.infer<typeof vehicleUseMetricCollectionConfiguration>
  export type vehicleUseMetricCollectionConfigurationUpdate = z.infer<typeof vehicleUseMetricCollectionConfigurationUpdate>
  export type ConfigurationType = (typeof configurationTypeOptions)[number]
}
